import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { clone, deepEquals } from '@jack-henry/frontend-utils/functions';
import { NavigationService } from '@treasury/core/navigation';
import { AchPayment, AchRecipientRecord, AchService } from '@treasury/domain/ach';
import { defaultAchRecurringPaymentFilterDto } from '@treasury/domain/ach/data/ach.data';
import { DownloadService } from '@treasury/domain/download';
import {
    FeatureVisibilityService,
    PwaVisibilityFeature,
} from '@treasury/domain/services/feature-visibility';
import { TmContainer } from '@treasury/presentation';
import { circlePlusIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-action-button';
import '@treasury/presentation/components/tm-download';
import { DownloadPageType } from '@treasury/presentation/components/tm-download.types';
import { css, html, nothing, PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import { AchPaymentFilterVm } from '../data/ach-payment-filter-vm';
import { AchRecipientActivityFilterVm } from '../data/ach-recipient-activity-filter-vm';
import '../partials/ach-payments-filter-sheet';
import '../partials/ach-payments-table';
import '../partials/ach-recipient-activity-table';
import '../partials/ach-recipient-filter-sheet';
import { AchTab } from '../types/ach-tab.enum';

export const tagName = 'ach-payments-container';
@customElement(tagName)
export class AchPaymentsContainer extends TmContainer {
    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private readonly navService: NavigationService;

    @InjectProperty({
        async: true,
        token: FeatureVisibilityService,
    })
    declare private featureVisibilityServicePromise: Promise<FeatureVisibilityService>;

    @state()
    private featureVisibilityService?: FeatureVisibilityService;

    @InjectProperty()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    declare private readonly downloadService: DownloadService<any>;

    @InjectProperty()
    declare private readonly achService: AchService;

    @state()
    private achPayments: AchPayment[] = [];

    @state()
    private recurringAchPayments: AchPayment[] = [];

    @state()
    private oneTimeTotals?: {
        totalCredit: number;
        totalDebit: number;
        count: number;
        status?: string;
    }[];

    @state()
    private recurringTotals?: {
        totalCredit: number;
        totalDebit: number;
        count: number;
        status?: string;
    }[];

    @state()
    private achRecipientActivity: AchRecipientRecord[] = [];

    @state()
    private filterSheetOpen = false;

    @state()
    private activeTab = AchTab.OneTime;

    @state()
    private tabs = [
        { label: 'One Time', id: AchTab.OneTime, enabled: true },
        { label: 'Recurring', id: AchTab.Recurring, enabled: true },
        { label: 'Recipient Activity', id: AchTab.RecipientActivity, enabled: false },
        { label: 'Templates', id: AchTab.Templates, enabled: false },
    ];

    @state()
    private loadingResults = {
        [AchTab.OneTime]: false,
        [AchTab.Recurring]: false,
        [AchTab.RecipientActivity]: false,
        [AchTab.Templates]: false,
    };

    @state()
    private stateLoaded = false;

    @state()
    private oneTimePaymentCount = 0;

    @state()
    private recurringPaymentCount = 0;

    @state()
    private recipientActivityCount = 0;

    private oneTimeFilter: AchPaymentFilterVm = new AchPaymentFilterVm();

    private recurringFilter: AchPaymentFilterVm = new AchPaymentFilterVm(
        defaultAchRecurringPaymentFilterDto
    );

    private recipientActivityFilter: AchRecipientActivityFilterVm =
        new AchRecipientActivityFilterVm();

    private get isRecurring() {
        return this.activeTab === AchTab.Recurring;
    }

    public async firstUpdated() {
        const { params } = await this.navService.getRouteData<{
            tab: AchTab;
            filter: AchPaymentFilterVm | AchRecipientActivityFilterVm;
        }>();
        const { tab: activeTab, filter: persistedFilter } = params;
        this.activeTab = activeTab || AchTab.OneTime;
        if (persistedFilter) {
            switch (this.activeTab) {
                case AchTab.OneTime:
                    this.oneTimeFilter = persistedFilter as AchPaymentFilterVm;
                    this.fetchAchPayments(AchTab.OneTime, this.oneTimeFilter);
                    break;
                case AchTab.Recurring:
                    this.recurringFilter = persistedFilter as AchPaymentFilterVm;
                    this.fetchAchPayments(AchTab.Recurring, this.recurringFilter);
                    break;
                case AchTab.RecipientActivity:
                    this.recipientActivityFilter = persistedFilter as AchRecipientActivityFilterVm;
                    this.fetchAchRecipientActivity(this.recipientActivityFilter);
                    break;
                default:
                    // No action needed for unknown tab types
                    break;
            }
        }
        this.stateLoaded = true;
        const paymentCount = this.isRecurring
            ? this.recurringPaymentCount
            : this.oneTimePaymentCount;
        this.headerService.configure({
            title: 'ACH Payments',
            menuItems: [
                {
                    title: 'Download',
                    icon: html`<tm-download
                        .page=${DownloadPageType.ACH}
                        .isRecurring=${this.isRecurring}
                        .filterModel=${this.isRecurring ? this.recurringFilter : this.oneTimeFilter}
                        .disabled=${paymentCount === 0}
                    ></tm-download>`,
                    action: () => undefined,
                },
            ],
        });
    }

    public async connectedCallback() {
        super.connectedCallback();
        this.featureVisibilityService = await this.featureVisibilityServicePromise;
        this.configureTabs();
    }

    protected render() {
        return html`${this.renderLoader()}
            <tm-sidebar>
                <div
                    class=${classMap({
                        'flex-grow flex flex-col h-full': true,
                        'py-2': this.isDesktop,
                    })}
                    slot="main-content"
                >
                    <div class="flex-none">
                        ${this.isDesktop ? nothing : this.renderActionItem()}
                    </div>
                    <div class="flex-grow">${this.renderTable()}</div>
                </div>
                <div slot="sidebar-content">
                    <div class="px-2 py-2 bg-primary rounded-lg">
                        <div class="flex justify-center mb-2 font-medium text-header">
                            Create ACH Payment
                        </div>
                        <div class="flex flex-col gap-2">
                            <tm-action-button
                                .buttonTitle=${'Create ACH Payment from Template'}
                                .buttonStyle=${'secondary'}
                                @click=${() => {
                                    this.navService.navigate('/ach/templates/initiate');
                                }}
                            ></tm-action-button>
                        </div>
                    </div>
                </div>
            </tm-sidebar>
            ${this.renderFilterSheets()}`;
    }

    protected async updated(changedProps: PropertyValues) {
        super.updated(changedProps);
        if (
            changedProps.has('activeTab') ||
            changedProps.has('achPayments') ||
            changedProps.has('recurringAchPayments')
        ) {
            if (this.activeTab === AchTab.RecipientActivity) {
                this.downloadService.updateConfig({
                    filterModel: undefined,
                    isRecurring: false,
                    disabled: true,
                });
            } else {
                this.downloadService.updateConfig({
                    filterModel: this.isRecurring ? this.recurringFilter : this.oneTimeFilter,
                    isRecurring: this.isRecurring,
                    disabled: this.isRecurring
                        ? !this.recurringPaymentCount
                        : !this.oneTimePaymentCount,
                });
            }
        }

        if (changedProps.has('activeTab') && this.activeTab === 'Templates') {
            this.navService.navigate(`/ach/templates`);
        }
    }

    private isFeatureVisible(feature: PwaVisibilityFeature): boolean {
        return this.featureVisibilityService
            ? this.featureVisibilityService.isFeatureVisible(feature)
            : false;
    }

    private async fetchAchPayments(selectedTab: AchTab, filter: AchPaymentFilterVm) {
        this.loadingResults[selectedTab] = true;
        try {
            if (selectedTab === AchTab.OneTime) {
                const response = await this.achService.getPaymentsAndTotals(
                    selectedTab,
                    filter.toDto()
                );
                this.achPayments = response.payments || [];
                this.oneTimeTotals = response.totals;
                this.oneTimePaymentCount = this.achPayments.length;
                this.oneTimeFilter = clone(filter);
            } else {
                const response = await this.achService.getPaymentsAndTotals(
                    selectedTab,
                    filter.toDto()
                );
                this.recurringAchPayments = response.payments || [];
                this.recurringTotals = response.totals;
                this.recurringPaymentCount = this.recurringAchPayments.length;
                this.recurringFilter = clone(filter);
            }
            this.configureTabs();
        } catch {
            this.notificationService.renderError('Failed to fetch ACH payments');
        } finally {
            this.loadingResults[selectedTab] = false;

            this.loading = false;
        }
    }

    private async fetchAchRecipientActivity(filter: AchRecipientActivityFilterVm) {
        this.loadingResults[AchTab.RecipientActivity] = true;

        try {
            const response = await this.achService.getRecipients(filter.toDto());
            const { achRecipients, limitedBatches, limitedPayments } = response;
            this.achRecipientActivity = achRecipients || [];
            if (limitedBatches || limitedPayments) {
                this.notificationService.renderWarning(
                    `Recipients in ${limitedPayments ? 'Payments' : ''} ${limitedPayments && limitedBatches ? 'and ' : ''}${limitedBatches ? 'Batches' : ''} are limited to 5,000 results. Please narrow your search using the filter.`
                );
            }
            this.recipientActivityCount = this.achRecipientActivity.length;
            this.recipientActivityFilter = clone(filter);
            this.configureTabs();
        } catch {
            this.notificationService.renderError('Failed to fetch ACH recipient activity');
        } finally {
            this.loadingResults[AchTab.RecipientActivity] = false;
            this.loading = false;
        }
    }

    private configureTabs() {
        this.tabs.map(tab => {
            if (tab.id === AchTab.RecipientActivity) {
                tab.enabled = this.isFeatureVisible(PwaVisibilityFeature.AchRecipientActivity);
            }
            if (tab.id === AchTab.Templates) {
                tab.enabled =
                    this.isFeatureVisible(PwaVisibilityFeature.AchTemplatesList) && this.isDesktop;
            }
        });

        if (!this.isDesktop) {
            this.tabs.map(tab => {
                if (tab.id === AchTab.OneTime) {
                    tab.label = `One Time (${this.oneTimePaymentCount})`;
                }
                if (tab.id === AchTab.Recurring) {
                    tab.label = `Recurring (${this.recurringPaymentCount})`;
                }
                if (tab.id === AchTab.RecipientActivity) {
                    tab.label = `Recipient Activity (${this.recipientActivityCount})`;
                }
            });
        }
    }

    private switchTab(tab: AchTab) {
        this.activeTab = tab;
    }

    private renderTabs() {
        const enabledTabs = this.tabs.filter(tab => tab.enabled);
        return html`<tm-tabs
            .tabs=${enabledTabs}
            .activeTab=${this.activeTab}
            @switchTab=${(e: CustomEvent) => {
                this.switchTab(e.detail.activeTab);
            }}
        ></tm-tabs>`;
    }

    private renderTable() {
        const tableAndTabs = html`<div class="flex flex-col h-full">
            <div
                class=${classMap({
                    'flex justify-between items-center border-b': true,
                    'bg-primary': !this.isDesktop,
                })}
            >
                ${this.renderTabs()}
                ${this.isDesktop
                    ? html`<tm-button
                          importance="primary"
                          .icon=${circlePlusIcon}
                          class="mr-4"
                          @click=${() => {
                              this.navService.navigate('/ach/templates/initiate');
                          }}
                          >Create ACH Payment from Template</tm-button
                      >`
                    : nothing}
            </div>
            <div class="grow">
                ${this.activeTab === AchTab.RecipientActivity
                    ? html`<ach-recipient-activity-table
                          class="h-full"
                          .loading=${this.loadingResults[AchTab.RecipientActivity]}
                          .isDesktop=${this.deviceService.isDesktop}
                          .achRecipientActivity=${this.achRecipientActivity}
                          @filter-ach-recipient-activity=${() => {
                              this.filterSheetOpen = true;
                          }}
                          .initiatedFilter=${!deepEquals(
                              this.recipientActivityFilter.toDto(),
                              new AchRecipientActivityFilterVm().toDto()
                          )}
                          @ach-recipient-transaction-select=${(e: CustomEvent) => {
                              if (e.detail.recordType === 'Template') {
                                  this.navService.navigate(
                                      `/ach/templates/detail/${e.detail.recordId}`,
                                      {
                                          tab: AchTab.RecipientActivity,
                                          filter: this.recipientActivityFilter,
                                      }
                                  );
                              } else {
                                  this.navService.navigate(`/ach/${e.detail.recordId}`, {
                                      tab: AchTab.RecipientActivity,
                                      filter: this.recipientActivityFilter,
                                  });
                              }
                          }}
                      ></ach-recipient-activity-table>`
                    : html` <ach-payments-table
                          class="h-full"
                          .loading=${this.loadingResults[this.activeTab]}
                          .totals=${this.activeTab === AchTab.OneTime
                              ? this.oneTimeTotals
                              : this.recurringTotals}
                          .achPayments=${this.activeTab === AchTab.OneTime
                              ? this.achPayments
                              : this.recurringAchPayments}
                          @filter-ach-payments=${() => {
                              this.filterSheetOpen = true;
                          }}
                          @ach-payment-select=${(e: CustomEvent) => {
                              this.navService.navigate(`/ach/${e.detail.id}`, {
                                  tab: this.activeTab,
                                  filter:
                                      this.activeTab === AchTab.OneTime
                                          ? this.oneTimeFilter
                                          : this.recurringFilter,
                              });
                          }}
                      ></ach-payments-table>`}
            </div>
        </div>`;
        if (this.deviceService.isDesktop) {
            return html`<tm-section
                class=${classMap({
                    'flex flex-col h-full': true,
                    'overflow-hidden bg-page': !this.isDesktop,
                    'mx-2 pb-2 rounded-lg': this.isDesktop,
                })}
                >${tableAndTabs}</tm-section
            >`;
        }
        return tableAndTabs;
    }

    private renderActionItem() {
        return html`<div>
            <tm-action-button
                class="my-2"
                .buttonTitle=${'Create ACH Payment from Template'}
                @click=${async () => {
                    await this.updateComplete;
                    this.navService.navigate('/ach/templates/initiate');
                }}
            ></tm-action-button>

            ${this.isFeatureVisible(PwaVisibilityFeature.AchTemplatesList)
                ? html` <tm-action-button
                      class="my-2"
                      .buttonTitle=${'Templates'}
                      @click=${async () => {
                          await this.updateComplete;
                          this.navService.navigate('/ach/templates/');
                      }}
                  ></tm-action-button>`
                : nothing}
        </div> `;
    }

    private renderFilterSheets() {
        if (!this.stateLoaded) {
            return nothing;
        }
        return html`<ach-payments-filter-sheet
                class=${classMap({ hidden: this.activeTab !== AchTab.OneTime })}
                .initial=${this.oneTimeFilter}
                .open=${this.filterSheetOpen}
                @close=${() => {
                    this.filterSheetOpen = false;
                }}
                @applyFilters=${async ({ detail }: CustomEvent) => {
                    if (!deepEquals(this.oneTimeFilter.toDto(), detail.toDto())) {
                        this.fetchAchPayments(AchTab.OneTime, detail);
                    }
                }}
            ></ach-payments-filter-sheet>
            <ach-payments-filter-sheet
                class=${classMap({ hidden: this.activeTab !== AchTab.Recurring })}
                .initial=${this.recurringFilter}
                .open=${this.filterSheetOpen}
                @close=${() => {
                    this.filterSheetOpen = false;
                }}
                @applyFilters=${async ({ detail }: CustomEvent) => {
                    if (!deepEquals(this.recurringFilter.toDto(), detail.toDto())) {
                        this.fetchAchPayments(AchTab.Recurring, detail);
                    }
                }}
            ></ach-payments-filter-sheet>
            ${this.isFeatureVisible(PwaVisibilityFeature.AchRecipientActivity)
                ? html` <ach-recipient-filter-sheet
                      class=${classMap({ hidden: this.activeTab !== AchTab.RecipientActivity })}
                      .initial=${this.recipientActivityFilter}
                      .open=${this.filterSheetOpen}
                      @close=${() => {
                          this.filterSheetOpen = false;
                      }}
                      @applyFilters=${({ detail }: CustomEvent) => {
                          if (!deepEquals(this.recipientActivityFilter.toDto(), detail.toDto())) {
                              this.fetchAchRecipientActivity(detail);
                          }
                      }}
                  ></ach-recipient-filter-sheet>`
                : nothing}`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    height: 100%;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AchPaymentsContainer;
    }
}
